import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, IconButton, Chip, InputAdornment, MenuItem, Switch,
  FormControlLabel, Tabs, Tab, Tooltip, alpha, Avatar, Divider, Skeleton, Paper
} from '@mui/material';
import {
  Add, Edit, Delete, Settings as SettingsIcon, Business, People,
  MedicalServices, Email as EmailIcon, Sms, Close, Save, CloudUpload, Delete as DeleteIcon,
  Security, ContentCopy, PersonAdd, Badge, LockReset, Shield
} from '@mui/icons-material';
import { companiesAPI, usersAPI, providerServicesAPI, settingsAPI, rolesAPI, securityAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { formatPhone, US_STATES } from '../../utils/formatters';

const TabPanel = ({ children, value, index }) => value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;

// Permission section builder
const PermissionSection = ({ title, color, section, perms, togglePerm, fields }) => (
  <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2.5, borderColor: alpha(color, 0.2) }}>
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
      {title}
    </Typography>
    <Grid container spacing={1}>
      {fields.map(f => (
        <Grid item xs={6} sm={4} md={3} key={f.key}>
          <FormControlLabel
            control={<Switch size="small" checked={!!perms[f.key]} onChange={() => togglePerm(section, f.key)}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: color } }} />}
            label={<Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{f.label}</Typography>}
          />
        </Grid>
      ))}
    </Grid>
  </Paper>
);

const PERMISSION_SECTIONS = [
  { title: 'Collections', color: '#f59e0b', section: 'collections', fields: [{ key: 'canAdd', label: 'Can Add' }, { key: 'canEdit', label: 'Can Edit' }, { key: 'canDelete', label: 'Can Delete' }, { key: 'canViewOwnOnly', label: 'Own Collections Only' }, { key: 'canViewBillingRate', label: 'View Billing Rate' }, { key: 'canViewInvoiceAmount', label: 'View Invoice Amount' }, { key: 'canViewProfit', label: 'View Profit' }, { key: 'canViewCommission', label: 'View Commission' }, { key: 'canViewProviderSection', label: 'View Provider Info' }, { key: 'canViewContractorSection', label: 'View Contractor Info' }] },
  { title: 'Providers', color: '#3b82f6', section: 'providers', fields: [{ key: 'canView', label: 'View' }, { key: 'canAdd', label: 'Add' }, { key: 'canEdit', label: 'Edit' }, { key: 'canDelete', label: 'Delete' }] },
  { title: 'Contractors', color: '#8b5cf6', section: 'contractors', fields: [{ key: 'canView', label: 'View' }, { key: 'canAdd', label: 'Add' }, { key: 'canEdit', label: 'Edit' }, { key: 'canDelete', label: 'Delete' }] },
  { title: 'Billing', color: '#10b981', section: 'billing', fields: [{ key: 'canView', label: 'View' }, { key: 'canManage', label: 'Manage' }] },
  { title: 'Credentialing', color: '#06b6d4', section: 'credentialing', fields: [{ key: 'canView', label: 'View' }, { key: 'canManage', label: 'Manage' }] },
  { title: 'Reports', color: '#f97316', section: 'reports', fields: [{ key: 'canView', label: 'View' }] },
  { title: 'Settings', color: '#6b7280', section: 'settings', fields: [{ key: 'canView', label: 'View' }, { key: 'canManage', label: 'Manage' }] },
];

const ROLE_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#dc2626', '#f97316', '#06b6d4', '#ec4899', '#6366f1', '#14b8a6', '#64748b', '#84cc16'];

const emptyPermissions = () => ({
  collections: { canAdd: false, canEdit: false, canDelete: false, canViewOwnOnly: false, canViewBillingRate: false, canViewInvoiceAmount: false, canViewProfit: false, canViewCommission: false, canViewProviderSection: false, canViewContractorSection: false },
  providers: { canAdd: false, canEdit: false, canDelete: false, canView: false },
  contractors: { canAdd: false, canEdit: false, canDelete: false, canView: false },
  billing: { canView: false, canManage: false },
  credentialing: { canView: false, canManage: false },
  reports: { canView: false },
  settings: { canView: false, canManage: false },
});

const Settings = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.isSuperAdmin || currentUser?.role === 'super_admin';
  const isAdmin = isSuperAdmin || currentUser?.role === 'admin';
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState({});
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [roles, setRoles] = useState([]);

  // User dialog
  const [userOpen, setUserOpen] = useState(false);
  const [userForm, setUserForm] = useState({ username: '', password: '', firstName: '', lastName: '', email: '', phone: '', role: 'viewer', isActive: true });
  const [editingUser, setEditingUser] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);

  // Service dialog
  const [svcOpen, setSvcOpen] = useState(false);
  const [svcForm, setSvcForm] = useState({ name: '', code: '', description: '', isActive: true });
  const [editingSvc, setEditingSvc] = useState(null);
  const [deleteSvcId, setDeleteSvcId] = useState(null);

  // Email/SMS settings
  const [emailSettings, setEmailSettings] = useState({ smtpHost: '', smtpPort: '', smtpUser: '', smtpPass: '', fromEmail: '' });
  const [smsSettings, setSmsSettings] = useState({ twilioSid: '', twilioToken: '', twilioPhone: '' });

  // Role dialog
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', color: '#64748b', permissions: emptyPermissions() });
  const [editingRole, setEditingRole] = useState(null);
  const [deleteRoleId, setDeleteRoleId] = useState(null);

  // User permissions editor
  const [permOpen, setPermOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permData, setPermData] = useState({});

  const load = useCallback(async () => {
    try {
      const [c, u, s, r] = await Promise.all([
        companiesAPI.getAll().catch(() => ({ data: [] })),
        usersAPI.getAll().catch(() => ({ data: [] })),
        providerServicesAPI.getAll({ limit: 1000 }).catch(() => ({ data: [] })),
        rolesAPI.getAll().catch(() => ({ data: [] }))
      ]);
      const cArr = Array.isArray(c.data) ? c.data : c.data?.data || [];
      const comp = Array.isArray(cArr) ? cArr[0] || {} : cArr || {};
      setCompany(comp);
      setUsers(Array.isArray(u.data) ? u.data : u.data?.data || []);
      setServices(Array.isArray(s.data) ? s.data : s.data?.data || []);
      setRoles(Array.isArray(r.data) ? r.data : r.data?.data || []);
      if (comp.settings) {
        setEmailSettings({
          smtpHost: comp.settings.smtpHost || '', smtpPort: comp.settings.smtpPort || '',
          smtpUser: comp.settings.smtpUser || '', smtpPass: '', fromEmail: comp.settings.fromEmail || ''
        });
        setSmsSettings({
          twilioSid: comp.settings.twilioSid || '', twilioToken: '', twilioPhone: comp.settings.twilioPhone || ''
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Settings | MyPayPlus'; }, []);
  useEffect(() => { load(); }, [load]);

  // Company
  const saveCompany = async () => {
    try {
      if (company._id) await companiesAPI.update(company._id, company);
      else await companiesAPI.create(company);
      toast.success('Company saved!'); load();
    } catch (e) { toast.error('Save failed'); }
  };

  // Users
  const openUserDialog = (u) => {
    if (u) {
      setEditingUser(u._id);
      setUserForm({ username: u.username || '', password: '', firstName: u.firstName || '', lastName: u.lastName || '', email: u.email || '', phone: u.phone || '', role: u.role || 'viewer', isActive: u.isActive !== false });
    } else { setEditingUser(null); setUserForm({ username: '', password: '', firstName: '', lastName: '', email: '', phone: '', role: 'viewer', isActive: true }); }
    setUserOpen(true);
  };

  const saveUser = async () => {
    try {
      const d = { ...userForm }; if (!d.password) delete d.password;
      // If a custom role is selected, also copy its permissions
      const matchingRole = roles.find(r => r.name === d.role);
      if (matchingRole && !editingUser) {
        d.permissions = matchingRole.permissions;
      }
      if (editingUser) await usersAPI.update(editingUser, d);
      else await usersAPI.create(d);
      setUserOpen(false); load();
      toast.success(editingUser ? 'User updated' : 'User created');
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const deleteUser = async () => {
    try { await usersAPI.delete(deleteUserId); setDeleteUserId(null); load(); toast.success('User deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Reset this user\'s password? They will receive an email with temporary credentials.')) return;
    try {
      await securityAPI.resetUserPassword(userId);
      toast.success('Password has been reset. User will receive an email with temporary credentials.');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to reset password'); }
  };

  const handleToggle2FA = async (userId, enable) => {
    const action = enable ? 'enable' : 'disable';
    if (!window.confirm(`${enable ? 'Enable' : 'Disable'} 2FA for this user?`)) return;
    try {
      const res = await securityAPI.toggleUser2FA(userId, { enabled: enable });
      // Immediately update local user state from response
      if (res.data?.user) {
        setUsers(prev => prev.map(u => (u._id || u.id) === userId
          ? { ...u, ...res.data.user }
          : u
        ));
      }
      toast.success(`2FA ${action}d for user`);
      await load();
    } catch (e) { toast.error(e.response?.data?.message || `Failed to ${action} 2FA`); }
  };

  // Services
  const openSvcDialog = (s) => {
    if (s) {
      setEditingSvc(s._id);
      setSvcForm({ name: s.name || '', code: s.code || '', description: s.description || '', billingType: s.billingType || '%', isActive: s.isActive !== false });
    } else { setEditingSvc(null); setSvcForm({ name: '', code: '', description: '', billingType: '%', isActive: true }); }
    setSvcOpen(true);
  };

  const saveSvc = async () => {
    try {
      if (editingSvc) await providerServicesAPI.update(editingSvc, svcForm);
      else await providerServicesAPI.create(svcForm);
      setSvcOpen(false); load();
      toast.success(editingSvc ? 'Service updated' : 'Service created');
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const deleteSvc = async () => {
    try { await providerServicesAPI.delete(deleteSvcId); setDeleteSvcId(null); load(); toast.success('Service deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  // Email/SMS
  const saveEmail = async () => {
    try { await settingsAPI.updateEmail(emailSettings); toast.success('Email settings saved!'); } catch (e) { toast.error('Save failed'); }
  };
  const saveSms = async () => {
    try { await settingsAPI.updateSMS(smsSettings); toast.success('SMS settings saved!'); } catch (e) { toast.error('Save failed'); }
  };

  // Roles
  const openRoleDialog = (role) => {
    if (role) {
      setEditingRole(role._id);
      setRoleForm({ name: role.name || '', description: role.description || '', color: role.color || '#64748b', permissions: role.permissions || emptyPermissions() });
    } else {
      setEditingRole(null);
      setRoleForm({ name: '', description: '', color: ROLE_COLORS[Math.floor(Math.random() * ROLE_COLORS.length)], permissions: emptyPermissions() });
    }
    setRoleOpen(true);
  };

  const duplicateRole = (role) => {
    setEditingRole(null);
    setRoleForm({
      name: `${role.name} (copy)`,
      description: role.description || '',
      color: ROLE_COLORS[Math.floor(Math.random() * ROLE_COLORS.length)],
      permissions: JSON.parse(JSON.stringify(role.permissions || emptyPermissions()))
    });
    setRoleOpen(true);
  };

  const toggleRolePerm = (section, key) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [section]: { ...prev.permissions[section], [key]: !prev.permissions[section]?.[key] }
      }
    }));
  };

  const saveRole = async () => {
    try {
      if (!roleForm.name.trim()) return toast.error('Role name is required');
      if (editingRole) {
        await rolesAPI.update(editingRole, roleForm);
        toast.success('Role updated');
      } else {
        await rolesAPI.create(roleForm);
        toast.success('Role created');
      }
      setRoleOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const handleDeleteRole = async () => {
    try {
      await rolesAPI.delete(deleteRoleId);
      setDeleteRoleId(null); load();
      toast.success('Role deleted');
    } catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
  };

  const seedDefaults = async () => {
    try {
      const res = await rolesAPI.seedDefaults();
      toast.success(res.data?.message || 'Default roles created');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const applyRoleToUser = async (roleId, userId) => {
    try {
      await rolesAPI.applyToUser(roleId, userId);
      toast.success('Role applied to user');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  // User permissions editor
  const openPermEditor = (u) => {
    setPermUser(u);
    setPermData(u.permissions || emptyPermissions());
    setPermOpen(true);
  };

  const togglePerm = (section, key) => {
    setPermData(prev => ({ ...prev, [section]: { ...prev[section], [key]: !prev[section]?.[key] } }));
  };

  const handleSavePermissions = async () => {
    try {
      await usersAPI.updatePermissions(permUser._id, { permissions: permData });
      toast.success('Permissions updated');
      setPermOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to update permissions'); }
  };

  const roleColor = (roleName) => {
    const match = roles.find(r => r.name === roleName);
    if (match) return match.color;
    const map = { super_admin: '#dc2626', admin: '#3b82f6', manager: '#8b5cf6', billing_specialist: '#f59e0b', collector: '#10b981', viewer: '#94a3b8' };
    return map[roleName] || '#64748b';
  };

  const countPermsEnabled = (perms) => {
    if (!perms) return 0;
    let c = 0;
    Object.values(perms).forEach(section => {
      if (typeof section === 'object') Object.values(section).forEach(v => { if (v === true) c++; });
    });
    return c;
  };

  if (loading) return <Box>{[...Array(2)].map((_, i) => <Skeleton key={i} height={200} sx={{ mb: 2, borderRadius: 3 }} />)}</Box>;

  const label = (text) => (
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>{text}</Typography>
  );

  // Build role options for user creation dropdown
  const roleOptions = roles.length > 0
    ? roles.filter(r => r.isActive && r.name !== 'super_admin').map(r => ({ value: r.name, label: r.name.replace(/_/g, ' ') }))
    : [{ value: 'admin', label: 'Admin' }, { value: 'manager', label: 'Manager' }, { value: 'billing_specialist', label: 'Billing Specialist' }, { value: 'collector', label: 'Collector' }, { value: 'viewer', label: 'Viewer' }];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">Settings</Typography>
        <Typography variant="body2" color="text.secondary">Manage company settings and configurations</Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ px: 3, pt: 1, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' },
            '& .MuiTabs-indicator': { bgcolor: '#3b82f6', height: 3, borderRadius: 1.5 } }}>
          <Tab icon={<Business sx={{ fontSize: 18 }} />} iconPosition="start" label="Company" />
          <Tab icon={<People sx={{ fontSize: 18 }} />} iconPosition="start" label="Users" />
          <Tab icon={<MedicalServices sx={{ fontSize: 18 }} />} iconPosition="start" label="Services" />
          <Tab icon={<Badge sx={{ fontSize: 18 }} />} iconPosition="start" label="Roles & Access" />
          <Tab icon={<EmailIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Email" />
          <Tab icon={<Sms sx={{ fontSize: 18 }} />} iconPosition="start" label="SMS" />
        </Tabs>
      </Card>

      {/* Company Tab */}
      <TabPanel value={tab} index={0}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2.5}>
              {/* Logo */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>Company Logo</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {company.logo ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box component="img" src={company.logo} alt="Logo" sx={{ height: 80, maxWidth: 200, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1 }} />
                      <IconButton size="small" onClick={async () => { try { await companiesAPI.deleteLogo(company._id); setCompany(c => ({ ...c, logo: '' })); toast.success('Logo removed'); } catch (e) { toast.error('Failed to remove'); } }}
                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#ef4444', color: 'white', width: 22, height: 22, '&:hover': { bgcolor: '#dc2626' } }}>
                        <Close sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ height: 80, width: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>No logo</Typography>
                    </Box>
                  )}
                  <Button variant="outlined" component="label" startIcon={<CloudUpload />} sx={{ fontWeight: 600 }}>
                    Upload Logo
                    <input type="file" hidden accept="image/*" onChange={async (e) => {
                      const file = e.target.files[0]; if (!file) return;
                      try { const res = await companiesAPI.uploadLogo(company._id, file); setCompany(res.data); toast.success('Logo uploaded'); }
                      catch (err) { toast.error('Upload failed'); }
                    }} />
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12} sm={6}>{label('Company Name')}<TextField fullWidth size="small" value={company.name || ''} onChange={e => setCompany(c => ({ ...c, name: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}>{label('Phone')}<TextField fullWidth size="small" placeholder="+1 (555) 000-0000" value={company.phone || ''} onChange={e => setCompany(c => ({ ...c, phone: formatPhone(e.target.value) }))} /></Grid>
              <Grid item xs={12} sm={6}>{label('Email')}<TextField fullWidth size="small" value={company.email || ''} onChange={e => setCompany(c => ({ ...c, email: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}>{label('Website')}<TextField fullWidth size="small" value={company.website || ''} onChange={e => setCompany(c => ({ ...c, website: e.target.value }))} /></Grid>
              <Grid item xs={12}>{label('Street Address')}<TextField fullWidth size="small" placeholder="123 Main St" value={company.street || ''} onChange={e => setCompany(c => ({ ...c, street: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={4}>{label('City')}<TextField fullWidth size="small" placeholder="New York" value={company.city || ''} onChange={e => setCompany(c => ({ ...c, city: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={3}>{label('State')}<TextField fullWidth size="small" select value={company.state || ''} onChange={e => setCompany(c => ({ ...c, state: e.target.value }))}><MenuItem value="">—</MenuItem>{US_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} sm={2}>{label('Zip')}<TextField fullWidth size="small" placeholder="10001" value={company.zip || ''} onChange={e => setCompany(c => ({ ...c, zip: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={3}>{label('Country')}<TextField fullWidth size="small" value={company.country || 'US'} onChange={e => setCompany(c => ({ ...c, country: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}>{label('Tax ID')}<TextField fullWidth size="small" value={company.taxId || ''} onChange={e => setCompany(c => ({ ...c, taxId: e.target.value }))} /></Grid>
              <Grid item xs={12} sm={6}>{label('NPI')}<TextField fullWidth size="small" value={company.npi || ''} onChange={e => setCompany(c => ({ ...c, npi: e.target.value }))} /></Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}><Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#334155' }}>Time Zone</Typography></Grid>
              <Grid item xs={12} sm={6}>
                {label('Company Time Zone')}
                <TextField fullWidth size="small" select value={company.settings?.timezone || 'America/New_York'}
                  onChange={e => setCompany(c => ({ ...c, settings: { ...c.settings, timezone: e.target.value } }))}>
                  {[
                    { value: 'America/New_York', label: 'Eastern Standard Time (EST)' },
                    { value: 'America/Chicago', label: 'Central Standard Time (CST)' },
                    { value: 'America/Denver', label: 'Mountain Standard Time (MST)' },
                    { value: 'America/Los_Angeles', label: 'Pacific Standard Time (PST)' },
                    { value: 'America/Anchorage', label: 'Alaska Standard Time (AKST)' },
                    { value: 'Pacific/Honolulu', label: 'Hawaii Standard Time (HST)' },
                    { value: 'America/Phoenix', label: 'Arizona (No DST)' },
                    { value: 'America/Puerto_Rico', label: 'Atlantic Standard Time (AST)' },
                    { value: 'UTC', label: 'Coordinated Universal Time (UTC)' },
                  ].map(tz => <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}><Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#334155' }}>Invoice Customization</Typography></Grid>
              <Grid item xs={12}>{label('Invoice Header Message')}<TextField fullWidth size="small" multiline rows={2} placeholder="e.g. Thank you for your business" value={company.invoiceHeaderMessage || ''} onChange={e => setCompany(c => ({ ...c, invoiceHeaderMessage: e.target.value }))} /></Grid>
              <Grid item xs={12}>{label('Invoice Notes / Details')}<TextField fullWidth size="small" multiline rows={2} placeholder="e.g. Payment terms, banking details" value={company.invoiceNotes || ''} onChange={e => setCompany(c => ({ ...c, invoiceNotes: e.target.value }))} /></Grid>
              <Grid item xs={12}>{label('Invoice Footer Message')}<TextField fullWidth size="small" multiline rows={2} placeholder="e.g. Please remit payment within 30 days" value={company.invoiceFooterMessage || ''} onChange={e => setCompany(c => ({ ...c, invoiceFooterMessage: e.target.value }))} /></Grid>
              <Grid item xs={12}>
                <Button variant="contained" startIcon={<Save />} onClick={saveCompany}
                  sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700, px: 4 }}>
                  Save Company
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Users Tab */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" startIcon={<Add />} onClick={() => openUserDialog(null)}
            sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700 }}>
            Add User
          </Button>
        </Box>
        <Card>
          <TableContainer>
            <Table>
              <TableHead><TableRow>
                <TableCell>Username</TableCell><TableCell>Full Name</TableCell><TableCell>Email</TableCell>
                <TableCell>Role</TableCell><TableCell>2FA</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u._id || u.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{u.username}</TableCell>
                    <TableCell>{u.firstName} {u.lastName}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>{u.email || '—'}</TableCell>
                    <TableCell><Chip label={(u.role || '').replace(/_/g, ' ')} size="small" sx={{ fontWeight: 700, height: 24, bgcolor: alpha(roleColor(u.role), 0.1), color: roleColor(u.role), textTransform: 'capitalize' }} /></TableCell>
                    <TableCell>
                      {u.totpEnabled ? (
                        <Chip icon={<Shield sx={{ fontSize: 14 }} />} label="Enabled" size="small" color="success" variant="outlined" sx={{ height: 24, fontSize: '0.72rem' }} />
                      ) : u.twoFaRequired ? (
                        <Chip label="Required" size="small" color="warning" variant="outlined" sx={{ height: 24, fontSize: '0.72rem' }} />
                      ) : (
                        <Chip label="Off" size="small" variant="outlined" sx={{ height: 24, fontSize: '0.72rem', color: '#94a3b8' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={u.isActive !== false ? 'Active' : 'Inactive'} size="small"
                        sx={{ fontWeight: 700, height: 24, bgcolor: u.isActive !== false ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1), color: u.isActive !== false ? '#059669' : '#dc2626' }} />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openUserDialog(u)} sx={{ color: '#8b5cf6' }}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                      {isSuperAdmin && (
                        <>
                          <Tooltip title="Reset Password"><IconButton size="small" onClick={() => handleResetPassword(u._id || u.id)} sx={{ color: '#f59e0b' }}><LockReset sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                          <Tooltip title={u.twoFaRequired || u.totpEnabled ? 'Disable 2FA' : 'Enable 2FA'}>
                            <IconButton size="small" onClick={() => handleToggle2FA(u._id || u.id, !(u.twoFaRequired || u.totpEnabled))} sx={{ color: u.twoFaRequired || u.totpEnabled ? '#10b981' : '#94a3b8' }}>
                              <Shield sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteUserId(u._id || u.id)} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </TabPanel>

      {/* Services Tab */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" startIcon={<Add />} onClick={() => openSvcDialog(null)}
            sx={{ background: 'linear-gradient(135deg, #10b981, #059669)', fontWeight: 700 }}>
            Add Service
          </Button>
        </Box>
        <Card>
          <TableContainer>
            <Table>
              <TableHead><TableRow>
                <TableCell>Name</TableCell><TableCell>Code</TableCell><TableCell>Type</TableCell><TableCell>Description</TableCell>
                <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {services.map(s => (
                  <TableRow key={s._id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                    <TableCell><Chip label={s.code || '—'} size="small" sx={{ fontWeight: 600, height: 22, fontFamily: 'monospace' }} /></TableCell>
                    <TableCell><Chip label={s.billingType === '$' ? 'Fixed $' : 'Percent %'} size="small" sx={{ fontWeight: 600, height: 22, bgcolor: s.billingType === '$' ? alpha('#3b82f6', 0.1) : alpha('#f59e0b', 0.1), color: s.billingType === '$' ? '#2563eb' : '#d97706' }} /></TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>{s.description || '—'}</TableCell>
                    <TableCell>
                      <Chip label={s.isActive !== false ? 'Active' : 'Inactive'} size="small"
                        sx={{ fontWeight: 700, height: 24, bgcolor: s.isActive !== false ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1), color: s.isActive !== false ? '#059669' : '#dc2626' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openSvcDialog(s)} sx={{ color: '#10b981' }}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteSvcId(s._id)} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </TabPanel>

      {/* ============= ROLES & ACCESS TAB ============= */}
      <TabPanel value={tab} index={3}>
        {/* Section 1: Role Templates */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Badge sx={{ fontSize: 20, color: '#8b5cf6' }} /> Role Templates
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>
                  Create and manage roles with pre-configured permissions. Assign roles to users to automatically set their access levels.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {roles.length === 0 && (
                  <Button size="small" variant="outlined" onClick={seedDefaults} sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                    Load Defaults
                  </Button>
                )}
                <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openRoleDialog(null)}
                  sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700, fontSize: '0.82rem' }}>
                  Create Role
                </Button>
              </Box>
            </Box>

            {roles.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed' }}>
                <Badge sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontWeight: 600, color: '#94a3b8', mb: 1 }}>No roles created yet</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#cbd5e1', mb: 2 }}>
                  Click "Load Defaults" to create standard roles, or "Create Role" to build a custom one.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Button size="small" variant="outlined" onClick={seedDefaults} sx={{ fontWeight: 600 }}>Load Defaults</Button>
                  <Button size="small" variant="contained" onClick={() => openRoleDialog(null)}
                    sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700 }}>Create Role</Button>
                </Box>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {roles.map(role => {
                  const usersInRole = users.filter(u => u.role === role.name).length;
                  const permCount = countPermsEnabled(role.permissions);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={role._id}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderColor: alpha(role.color || '#64748b', 0.3), transition: 'all 0.2s', '&:hover': { borderColor: role.color, boxShadow: `0 4px 12px ${alpha(role.color || '#64748b', 0.15)}` } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(role.color || '#64748b', 0.15), color: role.color || '#64748b', fontSize: '0.85rem', fontWeight: 700 }}>
                            {(role.name || '')[0]?.toUpperCase()}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {role.name.replace(/_/g, ' ')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {role.description || 'No description'}
                            </Typography>
                          </Box>
                          {role.isSystem && <Chip label="System" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha('#06b6d4', 0.1), color: '#06b6d4' }} />}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5 }}>
                          <Chip label={`${permCount} permissions`} size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(role.color || '#64748b', 0.08), color: role.color || '#64748b' }} />
                          <Chip label={`${usersInRole} user${usersInRole !== 1 ? 's' : ''}`} size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha('#64748b', 0.08), color: '#64748b' }} />
                        </Box>
                        <Divider sx={{ mb: 1 }} />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit Role">
                            <IconButton size="small" onClick={() => openRoleDialog(role)} sx={{ color: '#3b82f6' }}><Edit sx={{ fontSize: 16 }} /></IconButton>
                          </Tooltip>
                          <Tooltip title="Duplicate">
                            <IconButton size="small" onClick={() => duplicateRole(role)} sx={{ color: '#8b5cf6' }}><ContentCopy sx={{ fontSize: 16 }} /></IconButton>
                          </Tooltip>
                          {!role.isSystem && (
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => setDeleteRoleId(role._id)} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Section 2: User Permissions overview */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Security sx={{ fontSize: 20, color: '#3b82f6' }} /> User Permissions
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8', mb: 3 }}>
              Click the shield icon to customize individual user permissions, or use the role dropdown to quickly apply a role template.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Current Role</TableCell>
                    <TableCell>Apply Role</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell align="right">Edit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map(u => {
                    const p = u.permissions || {};
                    const permCount = countPermsEnabled(p);
                    return (
                      <TableRow key={u._id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: roleColor(u.role) }}>
                              {u.firstName?.[0]}{u.lastName?.[0]}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{u.firstName} {u.lastName}</Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>{u.username}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={(u.role || '').replace(/_/g, ' ')} size="small"
                            sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'capitalize', bgcolor: alpha(roleColor(u.role), 0.1), color: roleColor(u.role) }} />
                        </TableCell>
                        <TableCell>
                          {roles.length > 0 && (
                            <TextField select size="small" value="" displayEmpty
                              onChange={(e) => {
                                if (e.target.value) applyRoleToUser(e.target.value, u._id);
                              }}
                              sx={{ minWidth: 130, '& .MuiInputBase-root': { fontSize: '0.78rem', height: 32 } }}>
                              <MenuItem value="" disabled><em>Apply role...</em></MenuItem>
                              {roles.filter(r => r.isActive).map(r => (
                                <MenuItem key={r._id} value={r._id} sx={{ fontSize: '0.82rem' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color }} />
                                    <span style={{ textTransform: 'capitalize' }}>{r.name.replace(/_/g, ' ')}</span>
                                  </Box>
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                            <Chip label={`${permCount} enabled`} size="small"
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: permCount > 15 ? alpha('#10b981', 0.1) : permCount > 5 ? alpha('#f59e0b', 0.1) : alpha('#ef4444', 0.1), color: permCount > 15 ? '#059669' : permCount > 5 ? '#d97706' : '#dc2626' }} />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit Permissions">
                            <IconButton size="small" onClick={() => openPermEditor(u)} sx={{ color: '#3b82f6' }}>
                              <Security sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Email Tab */}
      <TabPanel value={tab} index={4}>
        <Card><CardContent sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>{label('SMTP Host')}<TextField fullWidth size="small" value={emailSettings.smtpHost || ''} onChange={e => setEmailSettings(s => ({ ...s, smtpHost: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>{label('SMTP Port')}<TextField fullWidth size="small" value={emailSettings.smtpPort || ''} onChange={e => setEmailSettings(s => ({ ...s, smtpPort: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>{label('Username')}<TextField fullWidth size="small" value={emailSettings.smtpUser || ''} onChange={e => setEmailSettings(s => ({ ...s, smtpUser: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>{label('Password')}<TextField fullWidth size="small" type="password" value={emailSettings.smtpPass || ''} onChange={e => setEmailSettings(s => ({ ...s, smtpPass: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>{label('From Email')}<TextField fullWidth size="small" value={emailSettings.fromEmail || ''} onChange={e => setEmailSettings(s => ({ ...s, fromEmail: e.target.value }))} /></Grid>
            <Grid item xs={12}>
              <Button variant="contained" startIcon={<Save />} onClick={saveEmail}
                sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700, px: 4 }}>Save Email Settings</Button>
            </Grid>
          </Grid>
        </CardContent></Card>
      </TabPanel>

      {/* SMS Tab */}
      <TabPanel value={tab} index={5}>
        <Card><CardContent sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>{label('Twilio SID')}<TextField fullWidth size="small" value={smsSettings.twilioSid || ''} onChange={e => setSmsSettings(s => ({ ...s, twilioSid: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>{label('Auth Token')}<TextField fullWidth size="small" type="password" value={smsSettings.twilioToken || ''} onChange={e => setSmsSettings(s => ({ ...s, twilioToken: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>{label('Phone Number')}<TextField fullWidth size="small" value={smsSettings.twilioPhone || ''} onChange={e => setSmsSettings(s => ({ ...s, twilioPhone: e.target.value }))} /></Grid>
            <Grid item xs={12}>
              <Button variant="contained" startIcon={<Save />} onClick={saveSms}
                sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700, px: 4 }}>Save SMS Settings</Button>
            </Grid>
          </Grid>
        </CardContent></Card>
      </TabPanel>

      {/* ===== DIALOGS ===== */}

      {/* User Dialog */}
      <Dialog open={userOpen} onClose={() => setUserOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', borderRadius: 2 }}><People sx={{ fontSize: 20 }} /></Avatar>
            <Typography sx={{ fontWeight: 700 }}>{editingUser ? 'Edit User' : 'Add User'}</Typography>
          </Box>
          <IconButton onClick={() => setUserOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>{label('Username *')}<TextField fullWidth size="small" value={userForm.username} onChange={e => setUserForm(f => ({ ...f, username: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>{label(editingUser ? 'New Password' : 'Password *')}<TextField fullWidth size="small" type="password" value={userForm.password} onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>{label('First Name *')}<TextField fullWidth size="small" value={userForm.firstName} onChange={e => setUserForm(f => ({ ...f, firstName: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>{label('Last Name *')}<TextField fullWidth size="small" value={userForm.lastName} onChange={e => setUserForm(f => ({ ...f, lastName: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>{label('Email')}<TextField fullWidth size="small" value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}>{label('Phone')}<TextField fullWidth size="small" placeholder="+1 (555) 000-0000" value={userForm.phone} onChange={e => setUserForm(f => ({ ...f, phone: formatPhone(e.target.value) }))} /></Grid>
            <Grid item xs={6}>
              {label('Role')}
              <TextField fullWidth size="small" select value={userForm.role} onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))}>
                {roleOptions.map(r => (
                  <MenuItem key={r.value} value={r.value} sx={{ textTransform: 'capitalize' }}>{r.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>{label('Status')}<FormControlLabel control={<Switch checked={userForm.isActive} onChange={e => setUserForm(f => ({ ...f, isActive: e.target.checked })) } />} label={userForm.isActive ? 'Active' : 'Inactive'} /></Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setUserOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={saveUser} sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700, px: 4 }}>{editingUser ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Service Dialog */}
      <Dialog open={svcOpen} onClose={() => setSvcOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: 2 }}><MedicalServices sx={{ fontSize: 20 }} /></Avatar>
            <Typography sx={{ fontWeight: 700 }}>{editingSvc ? 'Edit Service' : 'Add Service'}</Typography>
          </Box>
          <IconButton onClick={() => setSvcOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>{label('Service Name *')}<TextField fullWidth size="small" value={svcForm.name} onChange={e => setSvcForm(f => ({ ...f, name: e.target.value }))} /></Grid>
            <Grid item xs={6} sm={3}>{label('Code')}<TextField fullWidth size="small" value={svcForm.code} onChange={e => setSvcForm(f => ({ ...f, code: e.target.value }))} /></Grid>
            <Grid item xs={6} sm={3}>{label('Billing Type')}<TextField fullWidth size="small" select value={svcForm.billingType || '%'} onChange={e => setSvcForm(f => ({ ...f, billingType: e.target.value }))}><MenuItem value="%">Percent %</MenuItem><MenuItem value="$">Fixed $</MenuItem></TextField></Grid>
            <Grid item xs={12}>{label('Description')}<TextField fullWidth size="small" multiline rows={2} value={svcForm.description} onChange={e => setSvcForm(f => ({ ...f, description: e.target.value }))} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={svcForm.isActive} onChange={e => setSvcForm(f => ({ ...f, isActive: e.target.checked }))} />} label={svcForm.isActive ? 'Active' : 'Inactive'} /></Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSvcOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={saveSvc} sx={{ background: 'linear-gradient(135deg, #10b981, #059669)', fontWeight: 700, px: 4 }}>{editingSvc ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Role Dialog — Create / Edit Role */}
      <Dialog open={roleOpen} onClose={() => setRoleOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(roleForm.color || '#8b5cf6', 0.15), color: roleForm.color || '#8b5cf6', borderRadius: 2 }}>
            <Badge sx={{ fontSize: 22 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700 }}>{editingRole ? 'Edit Role' : 'Create New Role'}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              {editingRole ? 'Update role name, color, and permissions' : 'Define a role with custom permissions that can be assigned to users'}
            </Typography>
          </Box>
          <IconButton onClick={() => setRoleOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={5}>
              {label('Role Name *')}
              <TextField fullWidth size="small" placeholder="e.g. Senior Biller" value={roleForm.name} onChange={e => setRoleForm(f => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={5}>
              {label('Description')}
              <TextField fullWidth size="small" placeholder="What this role does" value={roleForm.description} onChange={e => setRoleForm(f => ({ ...f, description: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={2}>
              {label('Color')}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {ROLE_COLORS.map(c => (
                  <Box key={c} onClick={() => setRoleForm(f => ({ ...f, color: c }))}
                    sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: c, cursor: 'pointer', border: roleForm.color === c ? '3px solid #1e293b' : '2px solid transparent', transition: 'all 0.15s', '&:hover': { transform: 'scale(1.2)' } }} />
                ))}
              </Box>
            </Grid>
          </Grid>

          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security sx={{ fontSize: 18, color: '#3b82f6' }} /> Permission Configuration
          </Typography>

          {PERMISSION_SECTIONS.map(sec => (
            <PermissionSection key={sec.section} {...sec} perms={roleForm.permissions?.[sec.section] || {}} togglePerm={toggleRolePerm} />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRoleOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={saveRole}
            sx={{ background: `linear-gradient(135deg, ${roleForm.color || '#8b5cf6'}, ${roleForm.color || '#7c3aed'}dd)`, fontWeight: 700, px: 4 }}>
            {editingRole ? 'Update Role' : 'Create Role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Permissions Editor Dialog */}
      <Dialog open={permOpen} onClose={() => setPermOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: alpha('#3b82f6', 0.15), color: '#3b82f6', borderRadius: 2 }}>
            <Security sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Edit Permissions — {permUser?.firstName} {permUser?.lastName}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Role: {(permUser?.role || '').replace(/_/g, ' ')}</Typography>
          </Box>
          <IconButton onClick={() => setPermOpen(false)} sx={{ ml: 'auto' }} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {PERMISSION_SECTIONS.map(sec => (
            <PermissionSection key={sec.section} {...sec} perms={permData[sec.section] || {}} togglePerm={togglePerm} />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPermOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePermissions}
            sx={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)', fontWeight: 700 }}>Save Permissions</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialogs */}
      <Dialog open={!!deleteUserId} onClose={() => setDeleteUserId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete User?</DialogTitle>
        <DialogContent><Typography sx={{ color: 'text.secondary' }}>This action cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteUserId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={deleteUser} sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteSvcId} onClose={() => setDeleteSvcId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Service?</DialogTitle>
        <DialogContent><Typography sx={{ color: 'text.secondary' }}>This action cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteSvcId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={deleteSvc} sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteRoleId} onClose={() => setDeleteRoleId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Role?</DialogTitle>
        <DialogContent><Typography sx={{ color: 'text.secondary' }}>This will permanently remove this role. Users assigned to it will keep their current permissions but will no longer reference this role.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteRoleId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDeleteRole} sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
